import { useCharacterStore } from '@/engine/state';
import { obtenerHechizo } from './spellData';
import type { Spell, SpellContext } from '@/types';

export interface HechizoAnotado {
  spell: Spell;
  conocido: boolean;
  /** Si puede castearse dado el contexto + el mana actual. */
  puedeCastear: boolean;
  motivo: string | null;
}

/**
 * Anota todos los hechizos conocidos por el personaje, indicando
 * cuáles puede castear en el contexto dado.
 */
export function anotarHechizosConocidos(contexto: SpellContext): HechizoAnotado[] {
  const personaje = useCharacterStore.getState().personaje;
  const mana = personaje.stats.mana;

  return personaje.hechizosAprendidos
    .map((id) => obtenerHechizo(id))
    .filter((s): s is Spell => s !== undefined)
    .map((spell) => {
      const compatible =
        spell.contexto.includes(contexto) || spell.contexto.includes('cualquier');
      if (!compatible) {
        return {
          spell,
          conocido: true,
          puedeCastear: false,
          motivo: 'No aplica en este contexto.',
        };
      }
      if (mana < spell.costoMana) {
        return {
          spell,
          conocido: true,
          puedeCastear: false,
          motivo: `Faltan ${spell.costoMana - mana} de mana.`,
        };
      }
      return { spell, conocido: true, puedeCastear: true, motivo: null };
    });
}

export class HechizoNoCasteableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HechizoNoCasteableError';
  }
}

/**
 * Intenta castear un hechizo. Devuelve el hechizo casteado.
 * No aplica su efecto (lo hace el caller — típicamente combat o diálogo).
 * Solo deduce mana y valida.
 */
export function castearHechizo(spellId: string, contexto: SpellContext): Spell {
  const spell = obtenerHechizo(spellId);
  if (!spell) throw new HechizoNoCasteableError(`Hechizo inexistente: ${spellId}`);

  const personaje = useCharacterStore.getState().personaje;
  if (!personaje.hechizosAprendidos.includes(spellId)) {
    throw new HechizoNoCasteableError('No conocés ese hechizo.');
  }
  const compatible =
    spell.contexto.includes(contexto) || spell.contexto.includes('cualquier');
  if (!compatible) {
    throw new HechizoNoCasteableError('No aplica en este contexto.');
  }
  if (personaje.stats.mana < spell.costoMana) {
    throw new HechizoNoCasteableError('Mana insuficiente.');
  }

  useCharacterStore.getState().modificarStat('mana', -spell.costoMana);
  return spell;
}
